import ErrorComponent from "@/components/atoms/ErrorComponent";
import IconButton from "@/components/atoms/IconButton";
import CalendarBoard from "@/components/molecules/calendar/CalendarBoard";
import {
  CalendarFiltersButton,
  CalendarFiltersPanel,
  countActiveCalendarFilters,
  useCalendarFilterParams,
} from "@/components/molecules/calendar/CalendarFilters";
import StoreCompanyMeetingModal from "@/components/molecules/modals/company/StoreCompanyMeetingModal";
import MeetingDetailsModal from "@/components/molecules/modals/MeetingDetailsModal";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import useFrenchHolidays from "@/hooks/useFrenchHolidays";
import { getInitialUrlState, useTableFilters } from "@/hooks/useTableFilters";
import { MeetingFilters } from "@/interfaces/Meeting";
import { useGetMeetingsQuery } from "@/services/meetings";
import { useGetCalendarFilterableQuery } from "@/services/users";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { DatesSetArg, EventClickArg, EventInput } from "@fullcalendar/core";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import { ElementRef, FC, useRef, useState } from "react";
import { Col, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const CalendarView: FC = () => {
  const navigate = useNavigate();
  const StoreMeetingModalRef =
    useRef<ElementRef<typeof StoreCompanyMeetingModal>>(null);
  const DetailsModalRef = useRef<ElementRef<typeof MeetingDetailsModal>>(null);
  const user = useSelector(selectCurrentUser);
  const isAdminOrManager = checkIfUserHasRoles(user, [
    "ADMIN",
    "GENERALMANAGER",
  ]);
  const { data: filterablePool } = useGetCalendarFilterableQuery();
  const canFilterUsers = (filterablePool?.length ?? 0) > 0;
  const canSelectUsers = checkIfUserHasRoles(user, [
    "GENERALMANAGER",
    "HEADOFSALES",
    "CSM",
  ]);
  const defaultFilters = useRef({
    date: DateTime.now().toFormat("yyyy-MM"),
  }).current;

  const [filters, setFilters] = useState<MeetingFilters>(() => ({
    ...defaultFilters,
    ...getInitialUrlState(),
  }));

  const [showFilters, setShowFilters] = useState(false);

  const { onFiltersChange } = useTableFilters({
    tableName: "calendar",
    state: filters as any,
    onStateChange: setFilters as any,
    storage: "url",
    defaults: defaultFilters,
  });

  const activeFiltersCount = countActiveCalendarFilters(filters);

  const currentYear = parseInt(
    (filters.date || DateTime.now().toFormat("yyyy-MM")).slice(0, 4),
    10,
  );
  const holidayYears = [currentYear - 1, currentYear, currentYear + 1];
  const { holidays: frenchHolidays } = useFrenchHolidays(holidayYears);

  const debouncedQuery = useDebouncedValue(filters.query, 500);
  const filterParams = useCalendarFilterParams(filters);

  const { data, isLoading, isFetching, isError, error } = useGetMeetingsQuery({
    date: filters.date,
    query: debouncedQuery,
    ...filterParams,
  });

  const holidayEvents: EventInput[] = frenchHolidays.map((h) => ({
    id: `holiday-${h.date}`,
    title: `Férié - ${h.name}`,
    start: h.date,
    allDay: true,
    backgroundColor: "#dc3545",
    borderColor: "#dc3545",
    textColor: "#ffffff",
    classNames: ["fc-event-holiday"],
    extendedProps: { isHoliday: true },
  }));

  const allEvents: EventInput[] = [
    ...((data as EventInput[] | undefined) ?? []),
    ...holidayEvents,
  ];

  const onDatesSet = (arg: DatesSetArg) => {
    const currentStart = DateTime.fromJSDate(arg.view.currentStart);
    setFilters((old) => ({
      ...old,
      date: currentStart.toFormat("yyyy-MM"),
    }));
  };

  // Un rendez-vous sans entreprise n'a pas de fiche où aller : il s'ouvre dans
  // sa petite fiche (spec 2026-10-08, §2.6).
  const openStandalone = (
    id: string,
    start: string,
    props: Record<string, any>,
  ) =>
    DetailsModalRef.current?.showOrHiddenModal({
      id,
      title: props.meetingTitle,
      startAt: start,
      type: props.meetingType ?? null,
      authorId: props.authorId ?? null,
      authorName: props.authorName ?? null,
    });

  const onEventClick = (info: EventClickArg) => {
    if (info.event.url) {
      window.open(info.event.url);
      info.jsEvent.preventDefault();
    } else if (info.event.extendedProps.standalone) {
      openStandalone(info.event.id, info.event.startStr, info.event.extendedProps);
    } else if (info?.event.extendedProps.companyId) {
      navigate(`/companies/${info.event.extendedProps.companyId}`);
    }
  };

  const onDayModalRowClick = (event: EventInput) => {
    if ((event as any).standalone) {
      openStandalone(
        event.id as string,
        event.start as string,
        event as Record<string, any>,
      );
      return;
    }
    const companyId = (event as any).companyId as string | undefined;
    if (!companyId) return;
    navigate(`/companies/${companyId}`);
  };

  if (isError) {
    return <ErrorComponent error={error} absolute />;
  }

  return (
    <div className="formalys-page formalys-calendar-page">
      <StoreCompanyMeetingModal
        ref={StoreMeetingModalRef}
        calendarType="commercial"
      />
      <MeetingDetailsModal ref={DetailsModalRef} />
      <CalendarBoard
        name="commercial"
        events={allEvents}
        isLoading={isLoading}
        isFetching={isFetching}
        onEventClick={onEventClick}
        onDayModalRowClick={onDayModalRowClick}
        onSlotClick={(start) => StoreMeetingModalRef.current?.openAt(start)}
        onDatesSet={onDatesSet}
        headerSearch={
          <Col xs={12} md={3} className="order-md-2 mb-2 mb-md-0 mx-0 mx-md-3">
            <Form.Control
              type="search"
              placeholder="Rechercher un client..."
              size="sm"
              value={filters.query || ""}
              onChange={(e) =>
                setFilters((old) => ({ ...old, query: e.target.value }))
              }
            />
          </Col>
        }
        headerActions={
          <>
            <IconButton
              variant="nolina-primary"
              size="sm"
              icon={faPlus}
              transform="shrink-3"
              iconAlign="middle"
              className="me-2"
              aria-label="Nouveau rendez-vous"
              onClick={() => StoreMeetingModalRef.current?.showOrHiddenModal()}
            >
              <span className="ms-1 d-none d-sm-inline-block">Nouveau</span>
            </IconButton>
            <CalendarFiltersButton
              count={activeFiltersCount}
              onClick={() => setShowFilters(true)}
            />
          </>
        }
      />

      <CalendarFiltersPanel
        show={showFilters}
        onHide={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={onFiltersChange}
        showUserFilters={canFilterUsers || canSelectUsers}
        userOptions={isAdminOrManager ? "all" : "pool"}
      />
    </div>
  );
};

export default CalendarView;
